import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Brain, Shield, Smartphone, Wifi, WifiOff, Heart, Sparkles,
  MessageCircle, Database, Lock, ArrowRight, ArrowLeft,
  Lightbulb, Search, CheckCircle, Zap, RefreshCw
} from 'lucide-react';

const HowAIWorksPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <main className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-midnight-400 hover:text-cyclara-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 rounded-3xl bg-gradient-to-br from-cyclara-500 to-lavender-500
                      flex items-center justify-center mx-auto mb-6 shadow-xl shadow-cyclara-500/30"
          >
            <Brain className="w-12 h-12 text-white" />
          </motion.div>

          <span className="inline-block px-4 py-2 rounded-full bg-cyclara-100 text-cyclara-600
                        text-sm font-medium mb-4">
            AI Explained Simply
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-4">
            How Cyra <span className="gradient-text">Understands You</span>
          </h1>
          <p className="text-xl text-midnight-400 max-w-2xl mx-auto">
            A friendly guide to understanding the AI magic behind your personal wellness companion —
            no tech knowledge required!
          </p>
        </motion.div>

        {/* Welcome Note */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="bg-gradient-to-r from-cyclara-50 to-lavender-50 rounded-3xl p-8 mb-16 border border-cyclara-100"
        >
          <div className="flex items-start gap-4">
            <div className="text-4xl">☕</div>
            <div>
              <h2 className="text-xl font-bold text-midnight-500 mb-2">Welcome!</h2>
              <p className="text-midnight-400 leading-relaxed">
                Have you ever wondered how Cyra seems to understand you so well? How does she remember your name,
                know when you're on your period, and give advice that actually feels personal?
                This guide will explain everything in simple terms — like having a friendly chat over coffee!
              </p>
            </div>
          </div>
        </motion.div>

        {/* Section 1: What is AI */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-cyclara-100 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-cyclara-600" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500">What is AI, Really?</h2>
          </div>

          <div className="bg-white rounded-3xl shadow-xl shadow-midnight-500/5 border border-cyclara-100 p-8 mb-8">
            <h3 className="text-xl font-bold text-midnight-500 mb-4">
              🦜 Think of AI Like a Really Smart Parrot
            </h3>
            <p className="text-midnight-400 mb-6 leading-relaxed">
              Imagine a parrot that has listened to millions of conversations. Over time, this parrot learned:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                'How sentences are formed',
                'What words usually come after other words',
                'How people talk about different topics',
                'How to respond helpfully to questions'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-cyclara-50 rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-cyclara-500 flex-shrink-0" />
                  <span className="text-midnight-500">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-midnight-400 leading-relaxed">
              That's basically what an AI language model is! It's a computer program that has "read" billions of sentences
              and learned patterns of how humans communicate.
            </p>
          </div>

          <div className="bg-lavender-50 rounded-3xl p-8 border border-lavender-100">
            <h3 className="text-lg font-bold text-midnight-500 mb-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-lavender-500" />
              The Fancy Name: LLM (Large Language Model)
            </h3>
            <p className="text-midnight-400 leading-relaxed">
              When tech people say "LLM," they just mean a <strong>L</strong>arge <strong>L</strong>anguage <strong>M</strong>odel —
              a really big AI that understands and generates human language. Cyra uses a small but mighty LLM that lives
              <strong> right on your phone</strong> (more on why that's special later!).
            </p>
          </div>
        </motion.section>

        {/* Section 2: The Big Secret */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <Zap className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500">The Big Secret</h2>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-200 mb-8">
            <h3 className="text-xl font-bold text-midnight-500 mb-4">
              🤯 AI Has NO Memory!
            </h3>
            <p className="text-lg text-midnight-500 leading-relaxed">
              Here's something that surprises most people: <strong>AI doesn't actually remember anything between conversations!</strong>
              Every time you talk to Cyra, she starts with a completely blank slate. It's like meeting someone new every single time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Without App Help */}
            <div className="bg-red-50 rounded-3xl p-6 border border-red-200">
              <h4 className="font-bold text-red-700 mb-4 flex items-center gap-2">
                <span className="text-2xl">❌</span>
                Without the app's help
              </h4>
              <div className="bg-white rounded-xl p-4 font-mono text-sm space-y-2">
                <p><span className="text-cyclara-500">You:</span> "Hi Cyra!"</p>
                <p><span className="text-lavender-500">Cyra:</span> "Hello! 👋"</p>
                <p><span className="text-cyclara-500">You:</span> "My name is Sarah"</p>
                <p><span className="text-lavender-500">Cyra:</span> "Nice to meet you Sarah!"</p>
                <p className="text-midnight-300 italic">[You close the app]</p>
                <p className="text-midnight-300 italic">[You open it again]</p>
                <p><span className="text-cyclara-500">You:</span> "What's my name?"</p>
                <p><span className="text-red-500">Cyra:</span> "I don't know your name..." 😕</p>
              </div>
            </div>

            {/* With App Help */}
            <div className="bg-green-50 rounded-3xl p-6 border border-green-200">
              <h4 className="font-bold text-green-700 mb-4 flex items-center gap-2">
                <span className="text-2xl">✅</span>
                With Cyclara's memory system
              </h4>
              <div className="bg-white rounded-xl p-4 font-mono text-sm space-y-2">
                <p className="text-midnight-400 italic text-xs">
                  [Behind the scenes, the app tells Cyra: "The user's name is Sarah. She's on day 5 of her period. She often gets cramps."]
                </p>
                <div className="border-t border-green-100 my-3"></div>
                <p><span className="text-cyclara-500">You:</span> "What's my name?"</p>
                <p><span className="text-green-600">Cyra:</span> "You're Sarah! 💜" ✨</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-3xl p-6 border border-cyclara-100 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-cyclara-100 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-8 h-8 text-cyclara-600" />
              </div>
              <div>
                <h4 className="font-bold text-midnight-500 mb-2">The Magic Explained</h4>
                <p className="text-midnight-400 leading-relaxed">
                  <strong>The app is like Cyra's personal assistant</strong>, whispering helpful reminders in her ear before every conversation!
                  It tells Cyra about your name, your cycle phase, your common symptoms, and what has helped you before.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Section 3: Personal Profile */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-lavender-100 flex items-center justify-center">
              <Database className="w-6 h-6 text-lavender-600" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500">Building Your Personal Profile</h2>
          </div>

          <p className="text-lg text-midnight-400 mb-8 leading-relaxed">
            Every time you use Cyclara, it quietly learns about you. This creates your <strong>personal profile</strong>
            that makes Cyra feel like she truly knows you!
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                emoji: '🩸',
                title: 'From Your Period Tracking',
                items: ['When your periods usually start', 'How long they typically last', 'Whether your cycle is regular or irregular']
              },
              {
                emoji: '😊',
                title: 'From Your Mood Logs',
                items: ['What moods you often experience', 'Which symptoms you frequently have', 'Patterns like "often feels anxious before period"']
              },
              {
                emoji: '📝',
                title: 'From Your Journal',
                items: ['Topics you write about', 'How you express yourself', 'What helps you feel better']
              },
              {
                emoji: '💬',
                title: 'From Your Conversations',
                items: ['Your name (if you share it)', 'Things that help you cope', 'Important facts about your life']
              }
            ].map((category, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-3xl p-6 border border-cyclara-100 shadow-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{category.emoji}</span>
                  <h3 className="font-bold text-midnight-500">{category.title}</h3>
                </div>
                <ul className="space-y-2">
                  {category.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-midnight-400">
                      <ArrowRight className="w-4 h-4 text-cyclara-400 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Section 4: Similar Days */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-pink-100 flex items-center justify-center">
              <Search className="w-6 h-6 text-pink-600" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500">The Magic of "Similar Days"</h2>
          </div>

          <div className="bg-gradient-to-r from-pink-50 to-cyclara-50 rounded-3xl p-8 border border-pink-200 mb-8">
            <h3 className="text-xl font-bold text-midnight-500 mb-4">
              ✨ Ever Had Déjà Vu?
            </h3>
            <p className="text-midnight-400 leading-relaxed mb-6">
              Sometimes you feel a certain way and think, "Haven't I felt like this before?"
              Cyclara can actually find those similar moments in your history!
            </p>

            <div className="bg-white rounded-2xl p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-cyclara-500 text-white flex items-center justify-center font-bold text-sm">1</div>
                <div>
                  <p className="font-medium text-midnight-500">You log how you feel today:</p>
                  <p className="text-midnight-400 italic">"Anxious, intensity 4, with cramps and headache, day 22"</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-lavender-500 text-white flex items-center justify-center font-bold text-sm">2</div>
                <div>
                  <p className="font-medium text-midnight-500">Cyclara searches your history and finds:</p>
                  <p className="text-midnight-400 italic">"On December 22nd, you also felt anxious with cramps on day 22... and you wrote that a warm bath helped!"</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">3</div>
                <div>
                  <p className="font-medium text-midnight-500">Cyra can now say:</p>
                  <p className="text-cyclara-600 font-medium">"I notice you felt similarly last month around this time. That warm bath seemed to help — maybe worth trying again? 💜"</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-cyclara-100">
            <h4 className="font-bold text-midnight-500 mb-3 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-cyclara-500" />
              How It Works (The Simple Version)
            </h4>
            <p className="text-midnight-400 leading-relaxed">
              Cyclara converts your mood logs into special "fingerprints" — like a unique code that captures the <em>meaning</em> of how you felt.
              Then it can compare fingerprints to find similar feelings, even if you used different words!
            </p>
          </div>
        </motion.section>

        {/* Section 5: On-Device AI */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500">Why Your AI Lives ON Your Phone</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Most Apps */}
            <div className="bg-red-50 rounded-3xl p-6 border border-red-200">
              <div className="flex items-center gap-2 mb-4">
                <Wifi className="w-6 h-6 text-red-500" />
                <h3 className="font-bold text-red-700">Most Apps Work Like This</h3>
              </div>
              <div className="bg-white rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" /> Your Phone
                  </span>
                  <span className="text-red-400">→ Internet →</span>
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4" /> Company Servers
                  </span>
                </div>
                <div className="text-sm text-midnight-400 bg-red-50 rounded-lg p-3">
                  <p className="font-medium text-red-600 mb-1">Your data travels across the internet:</p>
                  <ul className="text-xs space-y-1">
                    <li>• "I'm feeling anxious today" → sent to the cloud</li>
                    <li>• Company processes your data</li>
                    <li>• Company stores your data</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cyclara */}
            <div className="bg-green-50 rounded-3xl p-6 border border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <WifiOff className="w-6 h-6 text-green-500" />
                <h3 className="font-bold text-green-700">Cyclara Works Differently</h3>
              </div>
              <div className="bg-white rounded-xl p-4">
                <div className="text-center mb-4">
                  <Smartphone className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="font-bold text-green-700">YOUR PHONE</p>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <Brain className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs font-medium">Cyra (AI Brain)</p>
                    <p className="text-xs text-green-600">Lives here!</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <Lock className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs font-medium">Your Data</p>
                    <p className="text-xs text-green-600">Stays here!</p>
                  </div>
                </div>
                <p className="text-xs text-green-700 text-center font-medium">
                  Everything happens here. Nothing leaves your phone.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-3xl p-8 border border-green-200">
            <h3 className="font-bold text-green-800 mb-4 text-lg">Why This Matters:</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Shield, text: 'Complete Privacy: Your period dates, moods, and thoughts NEVER leave your phone' },
                { icon: WifiOff, text: 'Works Offline: Chat with Cyra even on a plane or in the subway' },
                { icon: Lock, text: 'No Account Needed: No email, no password, no sign-up' },
                { icon: Heart, text: 'You Own Your Data: Export it, delete it — it\'s truly yours' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <item.icon className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Section 6: Real Examples */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-cyclara-100 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-cyclara-600" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500">Real Examples of Cyra in Action</h2>
          </div>

          <div className="space-y-6">
            {/* Example 1 */}
            <div className="bg-white rounded-3xl border border-cyclara-100 shadow-lg overflow-hidden">
              <div className="bg-cyclara-50 px-6 py-4 border-b border-cyclara-100">
                <h3 className="font-bold text-midnight-500">Example 1: Understanding Your Context</h3>
              </div>
              <div className="p-6 grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-red-500 mb-2">❌ Generic AI:</p>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-midnight-500 mb-2"><strong>You:</strong> "I feel so emotional today"</p>
                    <p className="text-midnight-400"><strong>AI:</strong> "I'm sorry to hear that. Would you like to talk about it?"</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-500 mb-2">✅ Cyra:</p>
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-midnight-500 mb-2"><strong>You:</strong> "I feel so emotional today"</p>
                    <p className="text-cyclara-600"><strong>Cyra:</strong> "Those emotional waves make so much sense right now, Sarah. You're on day 24 — just a few days before your period when progesterone drops and everything feels more intense. 💜 The last time you felt this way, you mentioned that journaling helped. Want to write about what's coming up?"</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Example 2 */}
            <div className="bg-white rounded-3xl border border-cyclara-100 shadow-lg overflow-hidden">
              <div className="bg-lavender-50 px-6 py-4 border-b border-lavender-100">
                <h3 className="font-bold text-midnight-500">Example 2: Remembering What Works</h3>
              </div>
              <div className="p-6 grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-red-500 mb-2">❌ Generic AI:</p>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-midnight-500 mb-2"><strong>You:</strong> "I have cramps again"</p>
                    <p className="text-midnight-400"><strong>AI:</strong> "Try a heating pad or pain medication."</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-500 mb-2">✅ Cyra:</p>
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-midnight-500 mb-2"><strong>You:</strong> "I have cramps again"</p>
                    <p className="text-cyclara-600"><strong>Cyra:</strong> "Oof, cramps on day 2 — that's usually your toughest day. 💜 I remember you mentioned last month that the heating pad on your lower back worked better than your belly. Have you tried that yet?"</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Example 3 */}
            <div className="bg-white rounded-3xl border border-cyclara-100 shadow-lg overflow-hidden">
              <div className="bg-amber-50 px-6 py-4 border-b border-amber-100">
                <h3 className="font-bold text-midnight-500">Example 3: Finding Patterns</h3>
              </div>
              <div className="p-6">
                <p className="text-sm font-medium text-green-500 mb-2">✅ What Cyra might notice:</p>
                <div className="bg-gradient-to-r from-cyclara-50 to-lavender-50 rounded-xl p-6">
                  <p className="text-cyclara-600 leading-relaxed">
                    "I've noticed something interesting, Sarah. The last three times you logged 'irritable' with intensity 4 or higher,
                    it was always between days 21-25 of your cycle, and you also had bloating. This seems to be a pattern for your luteal phase.
                    Would you like some tips specifically for this time in your cycle?"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Privacy Summary */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-midnight-500 rounded-3xl p-8 md:p-12 text-white">
            <h2 className="text-3xl font-bold mb-8 text-center">Your Privacy, Simplified</h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 rounded-2xl p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-red-300">
                  <Lock className="w-5 h-5" />
                  What Cyclara NEVER does:
                </h3>
                <ul className="space-y-3">
                  {[
                    'Send your data to the internet',
                    'Store your info on company servers',
                    'Share data with advertisers',
                    'Require an account or login',
                    'Track your usage or behavior'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/80">
                      <span className="text-red-400">❌</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/10 rounded-2xl p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-green-300">
                  <Shield className="w-5 h-5" />
                  What Cyclara ALWAYS does:
                </h3>
                <ul className="space-y-3">
                  {[
                    'Keep everything on YOUR phone',
                    'Let you export your data anytime',
                    'Let you delete everything easily',
                    'Work without internet connection',
                    'Respect your privacy completely'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/80">
                      <span className="text-green-400">✅</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Final Thoughts */}
        <motion.section
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          <div className="text-center">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-cyclara-500 to-lavender-500
                          flex items-center justify-center mx-auto mb-6 shadow-xl shadow-cyclara-500/30">
              <Heart className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-midnight-500 mb-4">That's the Magic of Cyclara</h2>
            <p className="text-lg text-midnight-400 max-w-2xl mx-auto mb-8 leading-relaxed">
              Smart AI that's truly yours — it learns about you, remembers your history, provides personalized support,
              and respects your privacy completely. Like having a knowledgeable friend who never judges and is always available, even offline. 💜
            </p>
            <Link
              to="/#download"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyclara-500 to-lavender-500
                       text-white font-semibold rounded-2xl shadow-lg shadow-cyclara-500/25
                       hover:shadow-xl hover:shadow-cyclara-500/30 transition-all"
            >
              <span>Download Cyclara Free</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.section>
      </div>
    </main>
  );
};

export default HowAIWorksPage;
